/**
 * In-match coaching for Rookie matches: when the grader flags a mistake, the
 * producer offers a one-line tip. Non-blocking, rare (max one per 30 s, each
 * tip at most twice per match), dismissable, and off for Pro/Legend.
 */
import './tutorial.css';
import type { GradeFeedback } from '../core/contracts';
import type { MatchSession } from '../game/session';
import { h } from '../ui/dom';

/** Grader feedback label → producer tip. `[K]` renders as a key cap. */
export const COACH_TIPS: Record<string, string> = {
  'MISSED IT!': 'When the ball nears the box, stay wide [1] or go to the 18-yard cam [3] / [4].',
  'OFF THE ACTION': "The ball's off screen. MAIN WIDE [1] is your safe home in open play.",
  'MISSED LIVE ACTION': "Only roll replays when the ball's dead. If play is live, [R] gets you back.",
  JUMPY: 'Let each shot breathe — hold a camera at least a couple of seconds before cutting again.',
  'LOST THE BALL': "Close-ups are for moments. Once play moves on, get back to wide [1].",
  'DEAD AIR': 'Long stoppage? Cut to a reaction — handhelds [7] / [8], or click the CROWD or BENCH monitor.',
  'CROSSED THE LINE': 'REVERSE [9] breaks the 180° rule in live play. Save it for replays.',
  'JUMP CUT': 'Two similar framings back to back look like a glitch. Change size: wide ↔ tight.',
  'NO REACTION': 'Big moment! Go tight on the player — [2], [7] or [8].',
  'LATE TO RESTART': "Play's restarting — be back on wide [1] before the kick.",
  'NO REPLAY': 'Ball out after a chance? That is your replay window — press [R].',
  'GOAL NOT REPLAYED': 'Every goal needs a replay. Roll it during the celebration with [R].',
  'NO SCOREBUG': 'The scorebug is off — press [S] to bring it back.',
  'NO SCORER GFX': 'After a goal, [Q] puts the scorer\'s name up.',
  'NO CARD GFX': 'After a card, [Q] names the player who was booked.',
  'NO SUB GFX': 'Substitution? [Q] names the player coming on.',
  CLUTTER: 'Attack building — clear graphics with [A] so the picture stays clean.',
  'BOOTH MISSED': 'Check the BOOTH panel: each request shows the key it needs.',
};

const COOLDOWN_MS = 30_000;
const LIFE_MS = 8_000;
const MAX_PER_TIP = 2;

export interface Coach { dispose(): void; readonly shown: number }

const headset = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13v-1a8 8 0 0 1 16 0v1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><rect x="2.6" y="12.5" width="4.2" height="6.5" rx="1.6" fill="currentColor"/><rect x="17.2" y="12.5" width="4.2" height="6.5" rx="1.6" fill="currentColor"/></svg>`;

function richText(text: string): HTMLElement {
  const el = h('div', { class: 'sd-coach__text' });
  const parts = text.split(/(\[[^\]]+\])/g);
  for (const p of parts) {
    const m = /^\[([^\]]+)\]$/.exec(p);
    if (m) el.append(h('kbd', { class: 'sd-pd__kbd', text: m[1]! }));
    else if (p) el.append(p);
  }
  return el;
}

export function createCoach(o: { session: MatchSession; root: HTMLElement; enabled: () => boolean; blocked?: () => boolean }): Coach {
  let lastAt = -Infinity;
  let shown = 0;
  const counts = new Map<string, number>();
  let cur: { el: HTMLElement; timer: number } | null = null;
  const startedAt = performance.now();

  function close() {
    if (!cur) return;
    const el = cur.el;
    clearTimeout(cur.timer);
    cur = null;
    el.classList.remove('is-on');
    setTimeout(() => el.remove(), 320);
  }

  function place(el: HTMLElement) {
    // Bottom of the side column (under the booth), else bottom-right.
    const side = document.querySelector<HTMLElement>('.sd-side')?.getBoundingClientRect();
    const w = el.offsetWidth, hh = el.offsetHeight;
    if (side && side.width > 40) {
      el.style.left = `${Math.round(side.left + (side.width - w) / 2)}px`;
      el.style.top = `${Math.round(side.bottom - hh - 12)}px`;
      el.style.width = `${Math.min(side.width - 16, w)}px`;
    } else {
      el.style.right = '16px';
      el.style.bottom = '56px';
    }
  }

  function show(label: string, tip: string) {
    close();
    const el = h('div', { class: 'sd-coach', attrs: { role: 'status', 'data-coach-tip': label } }, [
      h('span', { class: 'sd-pd__avatar', html: headset }),
      h('div', {}, [h('div', { class: 'sd-coach__head', text: `PRODUCER · TIP` }), richText(tip)]),
      h('button', { class: 'sd-coach__x', attrs: { type: 'button', title: 'Dismiss', 'aria-label': 'Dismiss tip' }, text: '✕', on: { click: close } }),
      h('div', { class: 'sd-coach__bar', style: `--life:${LIFE_MS}ms` }, [h('i')]),
    ]);
    o.root.append(el);
    place(el);
    requestAnimationFrame(() => el.classList.add('is-on'));
    cur = { el, timer: window.setTimeout(close, LIFE_MS) };
    shown++;
  }

  const off = o.session.onFeedback((f: GradeFeedback) => {
    if (!o.enabled() || o.blocked?.()) return;
    if (f.severity !== 'bad' && f.severity !== 'terrible') return;
    const tip = COACH_TIPS[f.label];
    if (!tip) return;
    const now = performance.now();
    if (now - startedAt < 6000 || now - lastAt < COOLDOWN_MS) return;
    const n = counts.get(f.label) ?? 0;
    if (n >= MAX_PER_TIP) return;
    counts.set(f.label, n + 1);
    lastAt = now;
    show(f.label, `Tip: ${tip}`);
  });

  return {
    get shown() { return shown; },
    dispose() { off(); close(); },
  };
}
